import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@shared/components/base-component/base.component';
import { WaitingChargeConfigApiService } from '@shared-features/services/waiting-charge-config-api.service';
import { UserType } from '@shared-features/enums/user-type.enum';
import { UpdateWaitingChargeConfigDto } from '@shared-features/interfaces';
import { WaitingChargeConfig } from '@shared-features/models/waiting-charge-config.model';

@Component({
  selector: 'app-waiting-charge-config',
  templateUrl: './waiting-charge-config.component.html',
  styleUrls: ['./waiting-charge-config.component.scss'],
})
export class WaitingChargeConfigComponent extends BaseComponent implements OnInit {
  @Input() configSource!: UserType;
  @Input() entityId!: number;
  form!: FormGroup;

  constructor(private waitingChargeConfigApiService: WaitingChargeConfigApiService) {
    super();
  }

  ngOnInit() {
    this.buildForm();
    this.fetchConfig();
  }

  private buildForm() {
    this.form = new FormGroup({
      enableWaitingTimeCharges: new FormControl(false),
      freeWaitingTime: new FormControl(null),
      hourlyRate: new FormControl(null),
      maximumWaitingHours: new FormControl(null),
      dailyRate: new FormControl(null),
    });

    this.form
      .get('enableWaitingTimeCharges')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((enabled: boolean) => this.updateValidators(enabled));
  }

  private fetchConfig() {
    this.load(this.waitingChargeConfigApiService.getConfig(this.configSource, this.entityId)).subscribe(
      (config) => {
        this.form.patchValue({
          enableWaitingTimeCharges: config.isEnabled,
          freeWaitingTime: config.gracePeriodHours,
          hourlyRate: config.hourRateSar,
          maximumWaitingHours: config.hourlyWindowHours,
          dailyRate: config.dailyRateSar,
        });
        this.updateValidators(config.isEnabled);
      }
    );
  }

  private updateValidators(enabled: boolean) {
    const freeWaitingTime = this.form.get('freeWaitingTime');
    const dailyRate = this.form.get('dailyRate');

    if (enabled) {
      freeWaitingTime?.setValidators([Validators.required, Validators.min(0)]);
      dailyRate?.setValidators([Validators.required, Validators.min(0.01)]);
    } else {
      freeWaitingTime?.clearValidators();
      dailyRate?.clearValidators();
    }

    freeWaitingTime?.updateValueAndValidity();
    dailyRate?.updateValueAndValidity();
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { enableWaitingTimeCharges, freeWaitingTime, hourlyRate, maximumWaitingHours, dailyRate } =
      this.form.value;

    const payload = new UpdateWaitingChargeConfigDto({
      isEnabled: enableWaitingTimeCharges,
      gracePeriodHours: freeWaitingTime,
      hourRateSar: hourlyRate,
      hourlyWindowHours: maximumWaitingHours,
      dailyRateSar: dailyRate,
    } as WaitingChargeConfig);

    this.load(
      this.waitingChargeConfigApiService.updateConfig(this.configSource, this.entityId, payload),
      { isLoadingTransparent: true }
    ).subscribe(
      () => {
        this.showSuccessMessage("SuccessMessages.WaitingChargeConfigUpdatedSuccessfully");
      },
      (error) =>{
        this.showErrorMessage(error);
      }
    );
  }
}
